import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import { useFieldError } from "Shared/Components/Form/Helpers/getFormErrors";
import CustomFormLabel from "Shared/Components/Inputs/CustomFormLabel";
import { Stack, type SxProps, type Theme, Typography } from "@mui/material";
import {
	LocalizationProvider,
	DatePicker as MuiDatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import {
	type Control,
	Controller,
	type FieldError,
	type FieldErrors,
	type FieldPath,
	type FieldValues,
	get,
} from "react-hook-form";

type DatePickerProps<FormType extends FieldValues> = {
	label?: string;
	name: FieldPath<FormType>;
	control: Control<FormType, object>;
	disabled?: boolean;
	readOnly?: boolean;
	errors: FieldErrors<FormType>;
	sx?: SxProps<Theme>;
	sxLabel?: SxProps<Theme>;
	slotProps?: Record<string, unknown>;
	customOnChange?: () => void;
	minDate?: Dayjs | string;
	maxDate?: Dayjs | string;
	orientation?: "row" | "column";
};

export const DatePicker = <FormType extends FieldValues>({
	label,
	name,
	control,
	disabled,
	readOnly,
	errors,
	sx,
	customOnChange,
	slotProps,
	minDate,
	maxDate,
	orientation = "column",
	sxLabel,
	...rest
}: DatePickerProps<FormType>) => {
	const { dateFormat, format } = useDateFormat();
	const { hasError, errorMessage } = useFieldError({
		fieldError: get(errors, name) as FieldError,
	});

	return (
		<Stack
			flexDirection={orientation}
			alignItems={orientation === "row" ? "center" : "flex-start"}
			gap={orientation === "row" ? 1 : 0}
		>
			{label && (
				<CustomFormLabel
					sx={{
						...sxLabel,
						whiteSpace: "nowrap",
						my: orientation === "row" ? 0 : undefined,
					}}
				>
					{label}
				</CustomFormLabel>
			)}
			<Controller
				name={name}
				control={control}
				render={({ field: { onChange, value } }) => (
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<MuiDatePicker
							value={value ? dayjs(value) : null}
							format={dateFormat}
							slotProps={{
								...slotProps,
								textField: {
									...(slotProps?.textField ?? {}),
									helperText: !!hasError ? (
										<Typography color="error" variant="caption">
											{errorMessage}
										</Typography>
									) : (
										""
									),
								},
							}}
							name={name.toString()}
							disabled={disabled}
							readOnly={readOnly}
							onChange={(
								value: string | number | Date | Dayjs | null | undefined,
							) => {
								onChange(
									format(dayjs(value).startOf("day"), "isoStringLocalTime"),
								);
								customOnChange?.();
							}}
							sx={{
								width: "100%",
								"& .MuiOutlinedInput-root": {
									"& fieldset": {
										borderColor: hasError ? "error.main" : undefined,
									},
								},
								...sx,
							}}
							minDate={minDate ? dayjs(minDate) : undefined}
							maxDate={maxDate ? dayjs(maxDate) : undefined}
							{...rest}
						/>
					</LocalizationProvider>
				)}
			/>
		</Stack>
	);
};
